import { useEffect, useRef, useState } from 'react';

const GEOFENCE_RADIUS_M = 150;  // auto-check-in when within 150m
const MAX_ACCURACY_M    = 200;  // skip GPS fixes worse than 200m accuracy

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R    = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a    =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Watches the device GPS and fires onEnter(visit, gpsCoords) the first time
 * the user enters the geofence radius of a planned visit.
 *
 * Visits and the callback are read through refs so the watchPosition never
 * restarts when the visit list updates — only one watch is started per mount.
 *
 * Returns { isWatching, permissionError }.
 */
export function useGeofencing(visits, onEnter) {
  const [isWatching,    setIsWatching]    = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  // Refs so the watchPosition callback always sees the latest values
  // without needing to restart the watch
  const visitsRef   = useRef(visits);
  const onEnterRef  = useRef(onEnter);
  const triggeredRef = useRef(new Set());

  useEffect(() => { visitsRef.current  = visits;  }, [visits]);
  useEffect(() => { onEnterRef.current = onEnter; }, [onEnter]);

  // Reset triggered set when all planned visits are gone (new day / all done)
  useEffect(() => {
    const planned = (visits || []).filter(v => v.visit_state === 'planned');
    if (planned.length === 0) triggeredRef.current = new Set();
  }, [visits]);

  // Single watchPosition for the lifetime of the component
  useEffect(() => {
    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setIsWatching(true);
        const { latitude, longitude, accuracy } = position.coords;

        if (accuracy > MAX_ACCURACY_M) return; // too inaccurate — skip

        const watchable = (visitsRef.current || []).filter(
          v => v.visit_state === 'planned'
            && v.entity_latitude  != null
            && v.entity_longitude != null
        );

        for (const visit of watchable) {
          if (triggeredRef.current.has(visit.id)) continue;
          const dist = haversineMeters(
            latitude, longitude,
            parseFloat(visit.entity_latitude),
            parseFloat(visit.entity_longitude),
          );
          if (dist <= GEOFENCE_RADIUS_M) {
            triggeredRef.current.add(visit.id);
            onEnterRef.current(visit, { latitude, longitude, accuracy });
          }
        }
      },
      (err) => {
        setPermissionError(err.message);
        setIsWatching(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
      setIsWatching(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally empty deps — visits/onEnter accessed via refs above

  return { isWatching, permissionError };
}
